import React, { useEffect, useState } from "react";
import { CheckCircle2, Send } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const SECTIONS = [
  { img: "portfolio", title: "Portfolio complessivo", text: "Spesa fatturata, consumi Power + Gas, costo medio, budget e forecast dell'anno, con l'andamento mese per mese." },
  { img: "budget", title: "Forecast vs Budget e Simulazione fixing", text: "Forecast Dinamico aggiornato ai prezzi forward, budget mensili modificabili, avvisi di superamento e simulazioni di chiusura dei prezzi." },
  { img: "costi", title: "Consumi & costi Power e Gas", text: "Trend della spesa, costo unitario, materia prima, confronti con l'anno precedente e tra punti di fornitura." },
  { img: "heatmap", title: "Heatmap e Profili di carico", text: "Curve orarie e a 15 minuti, heatmap giorni × ore, picchi, potenza, PUN e costo teorico per ogni ora." },
  { img: "gas-giornaliero", title: "Gas giornaliero & CG", text: "Consumi giornalieri dai Verbali di Misura SNAM, Capacità giornaliera conferita e giorni di supero." },
  { img: "esg", title: "KPI & Sostenibilità", text: "Quota rinnovabile, emissioni CO₂ Scope 1 e 2, emissioni evitate e Report ESG scaricabile." },
];

function Section({ s, i }) {
  return (
    <div className="rounded-2xl border border-slate-200 overflow-hidden bg-white" data-testid={`ea-promo-section-${s.img}`}>
      <a href={`/ea-preview/${s.img}.jpg`} target="_blank" rel="noopener noreferrer" className="block aspect-[16/9] bg-slate-100 overflow-hidden" title="Apri l'immagine intera" data-testid={`ea-promo-img-${s.img}`}>
        <img src={`/ea-preview/${s.img}.jpg`} alt={s.title} loading="lazy" className="w-full h-full object-cover object-top transition-transform duration-300 hover:scale-[1.03]" />
      </a>
      <div className="p-4">
        <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-purple-600">Sezione {i + 1}</div>
        <div className="font-display font-bold text-slate-900 mt-0.5">{s.title}</div>
        <div className="text-sm text-slate-600 mt-1 leading-relaxed">{s.text}</div>
      </div>
    </div>
  );
}

function RequestBox({ open }) {
  const [req, setReq] = useState(undefined);
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) api.get("/ea/activation-request").then(({ data }) => setReq(data)).catch(() => setReq(null)); }, [open]);
  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/ea/activation-request", { message: msg });
      setReq(data);
      toast.success(data.already ? "Richiesta già inviata nei giorni scorsi" : "Richiesta inviata ad Adybo");
    } catch (e) { toast.error(e?.response?.data?.detail || "Invio non riuscito"); }
    setBusy(false);
  };
  if (req?.status === "pending") return (
    <div className="flex items-start gap-3 rounded-xl bg-emerald-50 border border-emerald-200 p-4 text-sm text-emerald-800" data-testid="ea-promo-request-sent">
      <CheckCircle2 size={18} className="shrink-0 mt-0.5" />
      <div><b>Richiesta inviata</b> il {new Date(req.created_at).toLocaleDateString("it-IT")}. Il tuo referente Adybo ti contatterà per l'attivazione.</div>
    </div>
  );
  return (
    <div className="space-y-3">
      <textarea value={msg} onChange={(e) => setMsg(e.target.value)} maxLength={1000} rows={2} placeholder="Messaggio facoltativo (es. numero di punti di fornitura, orari in cui contattarti)"
        className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 outline-none" data-testid="ea-promo-message" />
      <button onClick={send} disabled={busy || req === undefined} className="btn-brand w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3 disabled:opacity-60" data-testid="ea-promo-request-btn">
        <Send size={16} /> {busy ? "Invio…" : "Richiedi attivazione"}
      </button>
    </div>
  );
}

export function EaPromoDialog({ open, onOpenChange }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto p-0 [&>button]:text-white/80 [&>button:hover]:text-white" data-testid="ea-promo-dialog">
        <div className="p-6 sm:p-8 text-white" style={{ background: "radial-gradient(ellipse at 85% 0%, #3A1C9E 0%, transparent 55%), #0A0F3D" }}>
          <DialogHeader className="text-left space-y-2">
            <div className="text-[10px] uppercase tracking-[0.2em] text-white/60 font-semibold">Energy Analytics · Modulo premium</div>
            <DialogTitle className="font-display text-2xl sm:text-3xl font-bold text-white">Tutti i dati dei tuoi punti di fornitura, in un'unica analisi</DialogTitle>
            <DialogDescription className="text-white/75 text-base leading-relaxed">
              Energy Analytics legge le tue fatture e le curve di consumo e le trasforma in cruscotti chiari: quanto spendi, come consumi, dove puoi risparmiare e quanto inquini. Con report PDF ed Excel pronti da condividere.
            </DialogDescription>
          </DialogHeader>
        </div>
        <div className="p-6 sm:p-8 space-y-6 bg-slate-50">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">{SECTIONS.map((s, i) => <Section key={s.img} s={s} i={i} />)}</div>
          <div className="rounded-2xl bg-white border border-purple-200 p-5 space-y-3" data-testid="ea-promo-request">
            <div className="font-display font-bold text-slate-900">Vuoi attivare Energy Analytics?</div>
            <div className="text-sm text-slate-600 leading-relaxed">L'attivazione del servizio <b>richiede un abbonamento a parte</b>, in aggiunta a Energy Monitor. Invia la richiesta: il tuo referente Adybo ti contatterà con la proposta e, dopo l'attivazione, caricherà i dati dei tuoi punti di fornitura.</div>
            <RequestBox open={open} />
          </div>
          <div className="text-xs text-slate-400">Immagini illustrative con dati di esempio.</div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
